import { memo, useCallback } from 'react';
import classNames from 'classnames';
import { BiCheckCircle } from 'react-icons/bi';
import {
  V2QsAbdLocationProps,
  defaultV2QsAbdLocationProps,
  abdLocationKeys,
  wholeAbdomenKey,
  otherKey,
} from './types';

/**
 * V2QsAbdLocation 컴포넌트
 *
 * 복부 위치 선택 컴포넌트
 * V1 QsAbdLocation의 V2 버전으로 순수 UI만 담당
 *
 * 주요 기능:
 * - 9개 복부 영역 선택 (이미지 오버레이)
 * - 전체 복부 옵션
 * - 기타 옵션
 */
function V2QsAbdLocation(props: V2QsAbdLocationProps) {
  const {
    value = [],
    onChange,
    abdomenImageUrl,
    wholeAbdomenLabel = defaultV2QsAbdLocationProps.wholeAbdomenLabel,
    otherLabel = defaultV2QsAbdLocationProps.otherLabel,
    disabled = defaultV2QsAbdLocationProps.disabled,
    readOnly = defaultV2QsAbdLocationProps.readOnly,
    hasError = defaultV2QsAbdLocationProps.hasError,
    errorMessage,
    className,
  } = props;

  const wholeSelected = value.includes(wholeAbdomenKey);
  const otherSelected = value.includes(otherKey);

  // 복부 영역 토글
  const handleLocationToggle = useCallback(
    (locationKey: string) => {
      if (disabled || readOnly || wholeSelected) return;

      const isSelected = value.includes(locationKey);
      let newValue: string[];

      if (isSelected) {
        newValue = value.filter((v) => v !== locationKey);
      } else {
        newValue = [...value, locationKey];
      }

      onChange?.(newValue);
    },
    [value, onChange, disabled, readOnly, wholeSelected]
  );

  // 전체 복부 토글
  const handleWholeToggle = useCallback(
    (checked: boolean) => {
      if (disabled || readOnly) return;

      const newValue: string[] = [];
      if (checked) {
        newValue.push(wholeAbdomenKey);
      }
      if (otherSelected) {
        newValue.push(otherKey);
      }

      onChange?.(newValue);
    },
    [onChange, disabled, readOnly, otherSelected]
  );

  // 기타 토글
  const handleOtherToggle = useCallback(
    (checked: boolean) => {
      if (disabled || readOnly) return;

      let newValue: string[];
      if (checked) {
        newValue = [...value, otherKey];
      } else {
        newValue = value.filter((v) => v !== otherKey);
      }

      onChange?.(newValue);
    },
    [value, onChange, disabled, readOnly]
  );

  return (
    <div className={classNames('relative max-w-md', className)}>
      {/* 복부 이미지 및 선택 영역 */}
      <div className="relative">
        {abdomenImageUrl && (
          <img src={abdomenImageUrl} alt="Abdomen regions" />
        )}
        <div className="absolute top-0 overflow-visible w-full">
          <div className="relative py-[3.1rem] px-[4.2rem] w-full flex flex-wrap left-1">
            {abdLocationKeys.map((locationKey, i) => {
              const selected = value.includes(locationKey);

              return (
                <div
                  key={locationKey}
                  className={classNames('min-w-[33.3%] aspect-square p-[2px]')}
                >
                  <div
                    className={classNames(
                      'flex-center w-full h-full rounded-2xl cursor-pointer',
                      'hover:bg-purple-200 hover:bg-opacity-40',
                      (disabled || readOnly || wholeSelected) && 'cursor-not-allowed opacity-50'
                    )}
                    onClick={() => handleLocationToggle(locationKey)}
                  >
                    <BiCheckCircle
                      size={33}
                      className={classNames(
                        selected ? 'opacity-90 text-purple-600' : 'opacity-10'
                      )}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 전체 복부 옵션 */}
      <div className="form-control">
        <label className="label cursor-pointer gap-2 py-3">
          <span className="label-text text-lg">{wholeAbdomenLabel}</span>
          <input
            type="checkbox"
            className={classNames(
              'checkbox checkbox-primary checkbox-sm',
              (disabled || readOnly) && 'cursor-not-allowed'
            )}
            checked={wholeSelected}
            disabled={disabled || readOnly}
            onChange={(e) => handleWholeToggle(e.target.checked)}
          />
        </label>
      </div>

      {/* 기타 옵션 */}
      <div className="form-control">
        <label className="label cursor-pointer gap-2 py-3">
          <span className="label-text text-lg">{otherLabel}</span>
          <input
            type="checkbox"
            className={classNames(
              'checkbox checkbox-primary checkbox-sm',
              (disabled || readOnly) && 'cursor-not-allowed'
            )}
            checked={otherSelected}
            disabled={disabled || readOnly}
            onChange={(e) => handleOtherToggle(e.target.checked)}
          />
        </label>
      </div>

      {/* 에러 메시지 */}
      {hasError && errorMessage && (
        <p className="text-error text-sm mt-2">{errorMessage}</p>
      )}
    </div>
  );
}

export default memo(V2QsAbdLocation);
